import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, FormControl, FormLabel, RadioGroup,
  FormControlLabel, Radio, MenuItem, Alert, CircularProgress, Box,
} from '@mui/material';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';
import { ScanGradeDialogNext } from './ScanNext.jsx';
import api from '../services/api.js';

const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';

const memberLabel = (member) => {
  if (!member) return '';
  const name = `${member.firstName || ''} ${member.lastName || ''}`.trim();
  const father = member.fatherName ? ` בן ${member.fatherName}` : '';
  return `${name}${father}`.trim() || `קוד ${member.personalCode}`;
};

const examLabel = (exam, gemaraSelection) => {
  if (exam) {
    const code = exam.code != null ? `[${exam.code}] ` : '';
    return `${code}${exam.name || ''}${exam.material ? ` — ${exam.material}` : ''}`.trim();
  }
  if (gemaraSelection?.length) {
    return gemaraSelection.map((s) => s.tractate).filter(Boolean).join(', ');
  }
  return 'ללא מבחן';
};

/**
 * חלונית הזנת ציון אחרי סריקת ברקוד.
 * candidates: [{ registrationId, exam, periodId, period, gemaraSelection }]
 */
/** In the new design the same props render ScanGradeDialogNext. */
const ScanGradeDialog = (props) => (useCkInside() ? <ScanGradeDialogNext {...props} /> : <ScanGradeDialogLegacy {...props} />);

const ScanGradeDialogLegacy = ({
  open,
  onClose,
  member,
  candidates = [],
  onSaved,
}) => {
  const [selectedRegId, setSelectedRegId] = useState('');
  const [tractate, setTractate] = useState('');
  const [score, setScore] = useState('');
  const [existingGrade, setExistingGrade] = useState(null);
  const [loadingGrade, setLoadingGrade] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selected = candidates.find((c) => c.registrationId === selectedRegId) || candidates[0];
  const gemara = selected?.gemaraSelection || [];
  const isGemara = IS_OPEN_GEMARA || gemara.length > 0;

  useEffect(() => {
    if (!open) return;
    setError('');
    setScore('');
    setExistingGrade(null);
    const first = candidates[0];
    setSelectedRegId(first?.registrationId || '');
    const firstTractate = first?.gemaraSelection?.[0]?.tractate || '';
    setTractate(firstTractate);
  }, [open, candidates]);

  useEffect(() => {
    if (!open || !member?._id || !selected) return undefined;

    let cancelled = false;
    const loadExisting = async () => {
      setLoadingGrade(true);
      setExistingGrade(null);
      try {
        const params = {
          memberId: member._id,
          all: 1,
        };
        if (selected.exam?._id) params.examId = selected.exam._id;
        if (selected.periodId) params.examPeriodId = selected.periodId;

        const { data } = await api.get('/grades', { params });
        const list = data?.data || [];
        const match = list.find((g) => {
          const gTractate = g.tractate || null;
          const want = isGemara ? (tractate || null) : null;
          return (gTractate || null) === (want || null);
        });
        if (!cancelled) {
          setExistingGrade(match || null);
          setScore(match ? String(match.score ?? '') : '');
        }
      } catch {
        if (!cancelled) setExistingGrade(null);
      } finally {
        if (!cancelled) setLoadingGrade(false);
      }
    };

    loadExisting();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [open, member?._id, selectedRegId, tractate]);

  const handleSave = async () => {
    setError('');
    if (!member?._id) return setError('חסר חבר');
    if (!selected) return setError('לא נמצאה הרשמה');
    if (isGemara && gemara.length > 0 && !tractate) {
      return setError('יש לבחור מסכת');
    }
    const s = parseFloat(score);
    if (Number.isNaN(s) || s < 0) return setError('ציון לא תקין (חייב להיות 0 ומעלה)');

    setSaving(true);
    try {
      if (existingGrade?._id) {
        await api.put(`/grades/${existingGrade._id}`, {
          score: s,
          tractate: isGemara ? (tractate || null) : null,
          version: existingGrade.version,
        });
      } else {
        const body = selected.exam?._id
          ? {
              memberId: member._id,
              examId: selected.exam._id,
              examPeriodId: selected.periodId || selected.exam.periodId || null,
              score: s,
              tractate: isGemara ? (tractate || null) : null,
            }
          : {
              memberId: member._id,
              examPeriodId: selected.periodId,
              score: s,
              tractate: tractate || null,
            };
        await api.post('/grades', body);
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאת שמירה');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>הזנת ציון</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {memberLabel(member)}
        </Typography>
        {member?.personalCode != null && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            קוד אישי: {member.personalCode}
          </Typography>
        )}

        {candidates.length > 1 && (
          <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
            <FormLabel component="legend">בחירת מבחן / הרשמה</FormLabel>
            <RadioGroup
              value={selectedRegId}
              onChange={(e) => {
                setSelectedRegId(e.target.value);
                const next = candidates.find((c) => c.registrationId === e.target.value);
                setTractate(next?.gemaraSelection?.[0]?.tractate || '');
                setScore('');
              }}
            >
              {candidates.map((c) => (
                <FormControlLabel
                  key={c.registrationId}
                  value={c.registrationId}
                  control={<Radio size="small" />}
                  label={examLabel(c.exam, c.gemaraSelection)}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}

        {candidates.length === 1 && selected && (
          <Box sx={{ mt: 1, mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              {examLabel(selected.exam, selected.gemaraSelection)}
            </Typography>
            {selected.period?.name && (
              <Typography variant="body2" color="text.secondary">
                זמן: {selected.period.name}
              </Typography>
            )}
          </Box>
        )}

        {isGemara && gemara.length > 0 && (
          <TextField
            select
            fullWidth
            size="small"
            label="מסכת"
            value={tractate}
            onChange={(e) => {
              setTractate(e.target.value);
              setScore('');
            }}
            sx={{ mt: 1, mb: 2 }}
          >
            {gemara.map((sel) => (
              <MenuItem key={sel.tractate} value={sel.tractate}>
                {sel.tractate}
              </MenuItem>
            ))}
          </TextField>
        )}

        {loadingGrade ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <TextField
            fullWidth
            size="small"
            label={existingGrade ? 'ציון (קיים — יעודכן)' : 'ציון'}
            type="number"
            value={score}
            onChange={(e) => setScore(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            autoFocus
            sx={{ mt: 1 }}
          />
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>ביטול</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loadingGrade || !selected}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          שמור ציון
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScanGradeDialog;
